/**
 * AdminCreateCustomerDialog
 * ─────────────────────────────────────────────────────────────────────────
 * Reusable dialog used by /admin/customers (and anywhere admins need to
 * spin up a customer profile manually, e.g. walk-ins, phone bookings).
 *
 * Validation is intentionally lightweight client-side — the backend
 * (POST /api/admin/customers) is the source of truth for normalization,
 * duplicate detection, and RBAC.
 *
 * Props:
 *   open       – boolean, controls Dialog visibility
 *   onOpenChange(next) – called when user closes (Esc / overlay / X)
 *   onCreated(customer) – called after a successful create; receives the new doc
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, UserPlus } from 'lucide-react';
import { adminApi } from '../../lib/api';

const CUSTOMER_TYPES = [
  { value: 'basic',         label: 'Basic' },
  { value: 'old_customer',  label: 'Old Customer' },
  { value: 'elite',         label: 'Elite' },
  { value: 'vip',           label: 'VIP' },
  { value: 'promoter',      label: 'Promoter' },
];

const INITIAL = {
  full_name:        '',
  phone:            '',
  email:            '',
  customer_type:    'basic',
  marketing_opt_in: false,
  notes:            '',
};

export default function AdminCreateCustomerDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm]       = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;   // works for inputs and Select onValueChange
    setForm((f) => ({ ...f, [k]: v }));
  };

  const reset = () => setForm(INITIAL);

  const validate = () => {
    if (!form.full_name.trim() || form.full_name.trim().length < 2) {
      toast.error('Enter a valid full name.');
      return false;
    }
    // Accept 10-digit or +91… form; backend will normalise.
    const digits = form.phone.replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Enter a valid phone number.');
      return false;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Enter a valid email address (or leave blank).');
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        full_name:        form.full_name.trim(),
        phone:            form.phone.trim(),
        email:            form.email.trim() || null,
        customer_type:    form.customer_type,
        marketing_opt_in: !!form.marketing_opt_in,
        notes:            form.notes.trim() || null,
      };
      const res = await adminApi.createCustomer(payload);
      const created = res?.data?.customer;
      toast.success(`Customer "${created?.full_name || form.full_name}" created.`);
      reset();
      onOpenChange?.(false);
      onCreated?.(created);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Could not create customer.';
      toast.error(typeof detail === 'string' ? detail : 'Could not create customer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange?.(next);
      }}
    >
      <DialogContent
        className="bg-[#0E0E0E] border-[#2A2A2A] text-[#F2F2F2] max-w-lg"
        data-testid="admin-create-customer-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-['EB_Garamond'] text-2xl flex items-center gap-2">
            <UserPlus size={20} className="text-[#D4A843]" />
            New Customer
          </DialogTitle>
          <DialogDescription className="text-[#B9B9B9]">
            Manually create a customer profile. They can later sign in via OTP using the phone you enter here.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="cust-name" className="text-xs uppercase tracking-wider text-[#8A8A8A]">
              Full Name *
            </Label>
            <Input
              id="cust-name"
              value={form.full_name}
              onChange={update('full_name')}
              placeholder="e.g. Aarav Sharma"
              className="bg-[#161616] border-[#2A2A2A] text-[#F2F2F2]"
              autoFocus
              data-testid="customer-create-name-input"
            />
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <Label htmlFor="cust-phone" className="text-xs uppercase tracking-wider text-[#8A8A8A]">
              Phone (IN) *
            </Label>
            <Input
              id="cust-phone"
              type="tel"
              inputMode="numeric"
              value={form.phone}
              onChange={update('phone')}
              placeholder="9876543210"
              className="bg-[#161616] border-[#2A2A2A] text-[#F2F2F2] font-mono"
              data-testid="customer-create-phone-input"
            />
            <p className="text-[11px] text-[#6F6F6F]">10-digit Indian mobile, or with +91 prefix.</p>
          </div>

          {/* Email (optional) */}
          <div className="space-y-1.5">
            <Label htmlFor="cust-email" className="text-xs uppercase tracking-wider text-[#8A8A8A]">
              Email <span className="text-[#6F6F6F] normal-case tracking-normal">(optional)</span>
            </Label>
            <Input
              id="cust-email"
              type="email"
              value={form.email}
              onChange={update('email')}
              placeholder="customer@example.com"
              className="bg-[#161616] border-[#2A2A2A] text-[#F2F2F2]"
              data-testid="customer-create-email-input"
            />
          </div>

          {/* Customer type */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#8A8A8A]">
              Customer Type
            </Label>
            <Select value={form.customer_type} onValueChange={(v) => setForm((f) => ({ ...f, customer_type: v }))}>
              <SelectTrigger
                className="bg-[#161616] border-[#2A2A2A] text-[#F2F2F2]"
                data-testid="customer-create-type-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0E0E0E] border-[#2A2A2A] text-[#F2F2F2]">
                {CUSTOMER_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Marketing opt-in */}
          <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border border-[#2A2A2A] bg-[#161616]">
            <div>
              <p className="text-sm text-[#F2F2F2]">Marketing opt-in</p>
              <p className="text-[11px] text-[#6F6F6F] mt-0.5">
                Include this customer in WhatsApp / SMS campaigns.
              </p>
            </div>
            <Switch
              checked={form.marketing_opt_in}
              onCheckedChange={(v) => setForm((f) => ({ ...f, marketing_opt_in: !!v }))}
              data-testid="customer-create-marketing-switch"
            />
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="cust-notes" className="text-xs uppercase tracking-wider text-[#8A8A8A]">
              Internal Notes <span className="text-[#6F6F6F] normal-case tracking-normal">(optional)</span>
            </Label>
            <Textarea
              id="cust-notes"
              value={form.notes}
              onChange={update('notes')}
              placeholder="e.g. Walk-in on 14 Jun; prefers corner table."
              className="bg-[#161616] border-[#2A2A2A] text-[#F2F2F2] min-h-[72px]"
              data-testid="customer-create-notes-input"
            />
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => { reset(); onOpenChange?.(false); }}
              className="border-[#2A2A2A] bg-transparent text-[#B9B9B9] hover:bg-[#161616]"
              data-testid="customer-create-cancel-button"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#D4A843] text-black hover:brightness-110 gap-2"
              disabled={submitting}
              data-testid="customer-create-submit-button"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              {submitting ? 'Creating…' : 'Create Customer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
